import React, { forwardRef, KeyboardEvent, useEffect, useRef, useState } from 'react'
import FormField from '../../../forms/form-field'
// TODO(NZTAJP-583): remove with UseNewPlacesApi flag — legacy widget import
import Autocomplete from 'react-google-autocomplete'
import { RouteClear } from '../route-clear/index'
import { useGoogleLoadState } from '../../../../stores/GoogleMapState'
import Icon from '../../../common/icon'
import { isNewPlacesApiEnabled } from '../../../../util/places-flag'
import {
  normalisePlaceResult,
  PredictionMatch,
  ResolvedPlace,
} from '../../../../util/places-service'
import { usePlaceAutocomplete } from '../../../../hooks/use-place-autocomplete'

export interface RouteInputProps {
  apiKey: string
  onPlaceSelected: (place: ResolvedPlace | null) => void
  options: google.maps.places.AutocompleteOptions & {
    types: never[]
    componentRestrictions: {
      country: string
    }
  }
  address?: string
  labelId: string
  label: string
  required: boolean
  inputId: string
  handleClear: React.MouseEventHandler<HTMLButtonElement> | undefined
  markerLabel: string
  hasValue: boolean
  invalidSearch?: boolean
  onKeyDown: React.KeyboardEventHandler<HTMLInputElement> | undefined
  placeholder: string
}

export type Ref = HTMLInputElement

// Address input field for the journey planner; renders the legacy or new autocomplete based on the UseNewPlacesApi flag and emits a unified ResolvedPlace to the consumer.
export const RouteInput = forwardRef<Ref, RouteInputProps>(
  (
    {
      labelId,
      label,
      required,
      inputId,
      apiKey,
      options,
      onPlaceSelected,
      handleClear,
      hasValue,
      invalidSearch,
      markerLabel,
      onKeyDown,
      placeholder,
      address,
    }: RouteInputProps,
    ref,
  ) => {
    const googleLoadState = useGoogleLoadState().get()
    const [isFocused, setIsFocused] = useState(false)
    const invalidFieldStyle = invalidSearch && hasValue === false ? 'c-text-input--invalid' : ''
    const className = `c-text-input c-text-input--has-btn ${invalidFieldStyle}`

    return (
      <div className='c-journey-form__field'>
        <div className='c-journey-form__field-marker'>
          <span className='c-journey-form__marker-label'>{markerLabel}</span>
          <Icon icon='marker' ariaHidden={true} />
        </div>
        <FormField labelId={labelId} label={label} htmlFor={inputId} isRequired={required}>
          <div className='c-input-container'>
            {googleLoadState &&
              // TODO(NZTAJP-583): remove the legacy branch (else) with UseNewPlacesApi flag
              (isNewPlacesApiEnabled() ? (
                <NewPlacesAutocomplete
                  ref={ref}
                  className={className}
                  inputId={inputId}
                  address={address ?? ''}
                  onPlaceSelected={onPlaceSelected}
                  onFocus={() => setIsFocused(true)}
                  onBlur={() => setIsFocused(false)}
                  onKeyDown={onKeyDown}
                  placeholder={placeholder}
                />
              ) : (
                <Autocomplete
                  className={className}
                  id={inputId}
                  ref={ref}
                  apiKey={apiKey}
                  onPlaceSelected={(p: google.maps.places.PlaceResult) =>
                    onPlaceSelected(normalisePlaceResult(p))
                  }
                  inputAutocompleteValue='off'
                  options={options}
                  onFocus={() => setIsFocused(true)}
                  onBlur={() => setIsFocused(false)}
                  onKeyDown={onKeyDown}
                  placeholder={placeholder}
                />
              ))}
            <RouteClear isVisible={isFocused || hasValue} handleClear={handleClear} />
          </div>
        </FormField>
      </div>
    )
  },
)

// Delay closing the listbox on blur so a click on a suggestion (mousedown -> blur) can fire first.
const SUGGESTION_CLICK_GRACE_MS = 150

interface NewPlacesAutocompleteProps {
  className: string
  inputId: string
  address: string
  onPlaceSelected: (place: ResolvedPlace | null) => void
  onFocus: () => void
  onBlur: () => void
  onKeyDown: React.KeyboardEventHandler<HTMLInputElement> | undefined
  placeholder: string
}

// New-API rendering branch: a controlled input plus listbox + Google attribution, driven by usePlaceAutocomplete.
const NewPlacesAutocomplete = forwardRef<HTMLInputElement, NewPlacesAutocompleteProps>(
  ({ className, inputId, address, onPlaceSelected, onFocus, onBlur, onKeyDown, placeholder }, ref) => {
    const {
      inputValue,
      setInputValue,
      suggestions,
      activeIndex,
      setActiveIndex,
      clearSuggestions,
      syncInputValue,
      selectSuggestion,
    } = usePlaceAutocomplete({ initialValue: address, onPlaceSelected })

    const blurTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
    const lastSyncedAddress = useRef(address)

    useEffect(() => {
      if (address !== lastSyncedAddress.current) {
        lastSyncedAddress.current = address
        syncInputValue(address)
      }
    }, [address, syncInputValue])

    // Cancel any pending blur timer so it doesn't fire after unmount
    useEffect(() => {
      return () => {
        if (blurTimer.current) clearTimeout(blurTimer.current)
      }
    }, [])

    const listboxId = `${inputId}-listbox`
    const showListbox = suggestions.length > 0

    const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
      if (showListbox) {
        switch (e.key) {
          case 'ArrowDown':
            e.preventDefault()
            setActiveIndex((i) => (i + 1) % suggestions.length)
            return
          case 'ArrowUp':
            e.preventDefault()
            setActiveIndex((i) => (i <= 0 ? suggestions.length - 1 : i - 1))
            return
          case 'Escape':
            e.preventDefault()
            clearSuggestions()
            return
          case 'Enter':
            if (activeIndex >= 0 && activeIndex < suggestions.length) {
              e.preventDefault()
              selectSuggestion(suggestions[activeIndex])
              onKeyDown?.(e)
              return
            }
            break
        }
      }
      onKeyDown?.(e)
    }

    return (
      <>
        <input
          className={className}
          id={inputId}
          ref={ref}
          type='text'
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onFocus={onFocus}
          onBlur={() => {
            blurTimer.current = setTimeout(() => {
              clearSuggestions()
              onBlur()
            }, SUGGESTION_CLICK_GRACE_MS)
          }}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          autoComplete='off'
          role='combobox'
          aria-autocomplete='list'
          aria-expanded={showListbox}
          aria-controls={listboxId}
          aria-activedescendant={
            showListbox && activeIndex >= 0 ? `${inputId}-option-${activeIndex}` : undefined
          }
        />
        {showListbox && (
          <div className='c-autocomplete'>
            <ul id={listboxId} role='listbox' className='c-autocomplete__list'>
              {suggestions.map((suggestion, index) => (
                <li
                  key={suggestion.placeId}
                  id={`${inputId}-option-${index}`}
                  role='option'
                  aria-selected={index === activeIndex}
                  className={`c-autocomplete__option${index === activeIndex ? ' c-autocomplete__option--active' : ''}`}
                  onMouseDown={(e) => {
                    e.preventDefault()
                    if (blurTimer.current) clearTimeout(blurTimer.current)
                    selectSuggestion(suggestion)
                  }}
                  onMouseEnter={() => setActiveIndex(index)}
                >
                  <span className='c-autocomplete__option-icon'>
                    <Icon icon='marker' ariaHidden={true} />
                  </span>
                  <span className='c-autocomplete__option-text'>
                    <span className='c-autocomplete__primary'>
                      {renderWithBoldedMatches(suggestion.primaryText, suggestion.primaryMatches)}
                    </span>
                    {suggestion.secondaryText && (
                      <span className='c-autocomplete__secondary'>{suggestion.secondaryText}</span>
                    )}
                  </span>
                </li>
              ))}
            </ul>
            <div className='c-autocomplete__attribution' aria-hidden='true'>
              <img
                src='https://maps.gstatic.com/mapfiles/api-3/images/powered-by-google-on-white3.png'
                alt='powered by Google'
              />
            </div>
          </div>
        )}
      </>
    )
  },
)

// Matches (per the SDK's offsets) may arrive in any order and can overlap, so we sort by start offset and walk
// the text left-to-right, using `cursor` to track how much of `text` has already been emitted.
function renderWithBoldedMatches(text: string, matches: PredictionMatch[]) {
  if (!matches.length) return text

  const sortedMatches = [...matches].sort((a, b) => a.startOffset - b.startOffset)
  const parts: React.ReactNode[] = []
  let cursor = 0

  sortedMatches.forEach((match, idx) => {
    // Clamp to the unconsumed region: handles overlapping matches and out-of-range offsets defensively.
    const start = Math.max(match.startOffset, cursor)
    const end = Math.min(match.endOffset, text.length)
    // Skip matches fully covered by a previous (overlapping) match.
    if (start >= end) return

    // Emit any plain text between the previous match and this one.
    if (start > cursor) parts.push(text.slice(cursor, start))
    // Emit the matched substring, bolded.
    parts.push(<strong key={`m${idx}`}>{text.slice(start, end)}</strong>)
    cursor = end
  })

  // Emit any trailing plain text after the final match.
  if (cursor < text.length) parts.push(text.slice(cursor))

  return parts
}

export default RouteInput
